import Debug from 'debug';
import { EventEmitter } from 'events';

import { Message } from '../../types/messages';
import { environment, getUrlsForEnvironment } from '../../environment';
import { Modal } from '../models/Modal';
import { Token, createTokenFromJWT } from '../models/Token';
import { User } from '../models/User';
import { Trial } from '../models/Trial';
import { TrialData, UserData } from '../../types/api';
import { isConfiguredForFidelity } from '../lib/host';
import { getAuthenticatedApiService } from './AuthenticatedApi';
import { MetricService } from './MetricService';
import { modalManager } from './ModalManager';
import { getSettingsService, AppSettingsService } from './SettingsService';

/**
 * State of the login
 *
 * - loggedin: user has been logged in
 * - loggedout: no current user, user logged out
 * - pending: user has initiated login flow
 * - loading: not-user-initiated login flow happening...
 */
export type LoginState = 'loggedin' | 'loggedout' | 'pending' | 'loading';
export type LogoutOptions = {
  triggered?: boolean;
  saveSettings?: boolean;
};

/**
 * Manage login and login-from-settings
 */
export class UserService extends EventEmitter {
  debug = Debug('o365:UserService');
  initializedCalled = false;
  userDetails: User | null = null;
  trialDetails: Trial | null = null;

  modalOptions: Office.DialogOptions = {
    displayInIframe: false,
    promptBeforeOpen: false,
    height: 60,
    width: 30,
  };

  protected initComplete = false;
  protected promises: Map<string, Promise<unknown>> = new Map();
  protected signals: Map<string, AbortController> = new Map();
  protected settings: AppSettingsService | null = null;
  protected state: LoginState = 'loggedout';
  protected tokenDetails: Token | null = null;
  protected stateParam: number | null = null;
  protected stateParamTimeout: ReturnType<typeof setTimeout> | null = null;
  protected loginListeners: Record<string, EventListener> = {};
  protected logoutListeners: Record<string, EventListener> = {};

  constructor() {
    super();
    this.debug('init');
  }

  // This was pulled out of the constructur due to a
  // race condition between setting event listeners
  // in useUserState
  initialize() {
    if (this.initializedCalled) {
      return;
    }
    this.debug('initializing UserService');
    this.initializedCalled = true;
    if (process.env.NODE_ENV !== 'test') {
      Office.onReady((info) => {
        if (info.platform && info.host) {
          this.initializeSettings();
          return;
        }
        this.debug('skipping settings initialization as user is not in outlook');
      });

      this.on('state', (state, previousState) => {
        this.debug('State change event emitted', state);
        if (state === 'loggedin' && previousState !== 'loggedin') {
          MetricService.increment('login.user.success');
        }
      });
    }
  }

  /**
   * Cleanup the state param so it is not reused.
   */
  destroyStateParam() {
    this.stateParam = null;
    if (this.stateParamTimeout) {
      window.clearTimeout(this.stateParamTimeout);
    }
  }

  /**
   * Add additional user data like trials
   */
  async expandUserData(): Promise<void> {
    if (!this.userDetails) {
      return;
    }

    // if the user is marked in trial, we will have to load trial data.
    if (this.userDetails.get('inTrial') === true) {
      const trial = await this.getTrialDetails();
      this.setTrial(trial ? new Trial(trial) : trial);
    }
    // otherwise, asynchronously get the trial data as we won't need it
    // show the correct data...
    else {
      this.getTrialDetails().then((trial) => {
        this.setTrial(trial ? new Trial(trial) : trial);
      });
    }
  }

  /**
   * Return the access token (or null)
   */
  getToken() {
    return this.tokenDetails ? this.tokenDetails.getAccessToken() : null;
  }

  /**
   * Return the token model.
   */
  getTokenObject() {
    return this.tokenDetails;
  }

  getTrial() {
    return this.trialDetails;
  }

  /**
   * Return trial details from the API.
   */
  async getTrialDetails(): Promise<TrialData | null> {
    this.debug('getTrialDetails() start');
    if (this.promises.has('getTrialDetails')) {
      this.debug('getTrialDetails(): reusing promise');
      return this.promises.get('getTrialDetails') as Promise<TrialData | null>;
    }

    const urls = getUrlsForEnvironment(environment);
    const api = getAuthenticatedApiService(environment, this, true);

    const ac = new AbortController();
    this.signals.set('getTrialDetails', ac);

    // get the data
    const promise = api.get(`${urls.apiUrl}/v2/user/trial/pvp`, { signal: ac.signal });
    this.promises.set('getTrialDetails', promise);
    const response = await promise;
    this.signals.delete('getTrialDetails');
    if (!response) {
      this.debug('getTrialDetails(): Could not getTrialDetails() from the API', response);
      this.promises.delete('getTrialDetails');
      return null;
    }

    try {
      const result = await response.json();
      this.debug('getTrialDetails(): result', result);
      this.promises.delete('getTrialDetails');
      return result;
    } catch (err) {
      this.debug('getTrialDetails(): error getting JSON', err);
      this.promises.delete('getTrialDetails');
      return null;
    }
  }

  /**
   * Ask API for Bombbomb for user details
   */
  async getUserDetails(): Promise<UserData | null> {
    this.debug('getUserDetails()');

    if (this.promises.has('getUserDetails')) {
      this.debug('getUserDetails(): reusing promise');
      return this.promises.get('getUserDetails') as Promise<UserData | null>;
    }

    const urls = getUrlsForEnvironment(environment);
    const api = getAuthenticatedApiService(environment, this, true);
    const errorMessage = 'There was an error logging in, please try again';

    const ac = new AbortController();
    this.signals.set('getUserDetails', ac);

    // get the data
    const promise = api.get(`${urls.apiUrl}/v2/accounts`, { signal: ac.signal });
    this.promises.set('getUserDetails', promise);
    const response = await promise;
    this.signals.delete('getUserDetails');
    if (!response) {
      this.debug('Could not getUserDetails() from the API', response);
      this.emit('error', errorMessage);
      this.promises.delete('getUserDetails');
      return null;
    }

    try {
      const result = await response.json();
      this.debug('getUserDetails()', result);
      this.promises.delete('getUserDetails');
      return result;
    } catch (err) {
      this.debug('getUserDetails(): error getting JSON', err);
      this.emit('error', errorMessage);
      this.promises.delete('getUserDetails');
      return null;
    }
  }

  /**
   * Return the stored user model.
   */
  getUser() {
    return this.userDetails;
  }

  /**
   * Load the settings and try to login the user
   */
  async initializeSettings(local = localStorage, roaming = Office.context.roamingSettings) {
    this.settings = getSettingsService('bombbomb', local, roaming);
    const result = await this.loginFromSettings();
    this.debug('Login state from settings', this.settings.get('loginState'));
    if (this.settings.get('loginState') !== 0) {
      this.stateParam = this.settings.get('loginState');
    }
    this.initComplete = true;
    this.emit('init-complete');
    return result;
  }

  /**
   * Is the user logged in?
   */
  isLoggedIn() {
    return this.state === 'loggedin' && !!this.userDetails && !!this.tokenDetails;
  }

  /**
   * Add event listeners to the login modal
   */
  listenToLoginModal(loginModal: Modal, signup = false) {
    this.loginListeners.message = this.onLoginMessage.bind(this, loginModal, signup);
    this.loginListeners.closed = this.onLoginClosed.bind(this, loginModal);
    loginModal.addListener('message', this.loginListeners.message);
    loginModal.addListener('closed', this.loginListeners.closed);
  }

  /**
   * Add event listeners to the logout modal
   */
  listenToLogoutModal(logoutModal: Modal) {
    this.logoutListeners.message = this.onLogoutMessage.bind(this, logoutModal);
    this.logoutListeners.closed = this.onLogoutClosed.bind(this, logoutModal);
    logoutModal.addListener('message', this.logoutListeners.message);
    logoutModal.addListener('closed', this.logoutListeners.closed);
  }

  /**
   * Login the user from the loggedin message we received on successful login.
   */
  async loginFromMessage(message: Message) {
    if (!message.params || !message.params.expires_in || !message.params.access_token) {
      return false;
    }

    // create a token from the JWT and check it's expiration (though hopefully
    // a new login isn't giving us an expired token)
    const token = createTokenFromJWT(message.params.access_token);
    if (!token || token.isExpired()) {
      this.debug('Could not create token from login message or token is expired', token);
      this.logout({ triggered: false });
      return false;
    }
    this.tokenDetails = token;

    const userDetails = await this.getUserDetails();
    if (!userDetails) {
      this.debug('Could not login from message: could not get user details');
      this.logout({ triggered: false });
      return false;
    }

    const user = new User(userDetails);
    if (!user.validate()) {
      this.debug('Could not validate user', user.invalidReason);
      this.logout({ triggered: false });
      return false;
    }

    this.userDetails = user;
    await this.expandUserData();
    this.settings?.setBatch({
      token: this.tokenDetails,
      user,
    });
    this.setState('loggedin');
    this.debug('Login completed successfully');
    return true;
  }

  /**
   * Check if settings has enough details to login the user.
   */
  async loginFromSettings() {
    if (!this.settings) {
      this.debug('no settings, leaving relogin...');
      this.logout({ triggered: false, saveSettings: false });
      return;
    }

    this.setState('loading');
    const token = this.settings.get('token');
    if (token && token.isExpired() === false) {
      this.tokenDetails = token;
    } else {
      // no token, no login
      this.debug('no token or token expiration could not be verified, leaving relogin...');
      this.logout({ triggered: false, saveSettings: false });
      return;
    }

    const user = this.settings.get('user');
    if (user) {
      this.userDetails = user;
    } else {
      this.debug('loginFromSettings: fetching fresh user');
      const userDetails = await this.getUserDetails();
      if (userDetails) {
        const user = new User(userDetails);
        if (!user.validate()) {
          this.debug('loginFromSettings: failed to validate user from token');
          this.logout({ triggered: false });
          return false;
        }
        this.userDetails = user;
        await this.expandUserData();
        this.settings?.set('user', this.userDetails);
      }
    }

    if (this.userDetails && this.tokenDetails) {
      this.debug('Login completed successfully');
      this.setState('loggedin');
      return true;
    }

    this.userDetails = null;
    this.tokenDetails = null;
    this.setState('loggedout');
    return false;
  }

  /**
   * A logout has occurred, either by user or internal issue
   */
  async logout(options: LogoutOptions = {}) {
    const logoutOptions: LogoutOptions = {
      triggered: true,
      saveSettings: true,
      ...options,
    };
    this.debug('logout() triggered by user?', logoutOptions.triggered);
    // We only need to go through Auth0
    // if the user triggered this logout.
    if (logoutOptions.triggered) {
      const logoutModal = await modalManager.open(
        'logout',
        `https://${window.location.host}/logout`,
        this.modalOptions,
      );
      this.listenToLogoutModal(logoutModal);
    }

    this.userDetails = null;
    this.tokenDetails = null;
    this.trialDetails = null;

    Object.entries(this.signals).forEach(([key, abort]) => {
      this.debug(`cancelling API request: ${key}`);
      abort.abort();
      this.signals.delete(key);
    });

    this.settings?.setBatch(
      {
        token: null,
        user: null,
        trial: null,
      },
      logoutOptions.saveSettings,
    );

    // turn off the showTrialBannerAfter setting if the user actually logged out
    logoutOptions.triggered && getSettingsService('ui').set('showTrialBannerAfter', 0, true);

    this.setState('loggedout');
    return true;
  }

  /**
   * User wants to log in!
   */
  onLoginClick = async () => {
    MetricService.increment('click.task_pane.log_in.button');
    this.resetStateParam();
    const params = new URLSearchParams({
      state: `${this.stateParam}`,
    });
    if (isConfiguredForFidelity()) {
      params.append('fidelity', '1');
      this.modalOptions.height = 45;
    }

    this.setState('pending');
    const loginModal = await modalManager.open(
      'login',
      `https://${window.location.host}/login?${params.toString()}`,
      this.modalOptions,
    );
    this.listenToLoginModal(loginModal);
  };

  onLoginCodeClick = async (loginCode: string) => {
    this.resetStateParam();
    const params = {
      state: `${this.stateParam}`,
      loginCode,
    };
    this.setState('pending');
    await this.settings?.set('loginState', this.stateParam ?? 0, true);

    this.debug(params.toString(), this.stateParam);
    const response = await fetch('/loginCode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params, null, 0),
    });

    if (response.ok) {
      const message = await response.json();
      this.debug('response ok', message);
      if (message.success) {
        this.debug('calling loginFromMessage', message);
        await this.loginFromMessage(message);
        return { success: true };
      } else {
        this.debug('login did not succeed');
        await this.logout({ triggered: false });
        return { success: false, message };
      }
    } else {
      this.debug('response not ok', response);
      await this.logout({ triggered: false });
      return { success: false, message: 'bad response from server' };
    }
  };

  onLoginClosed = (loginModal: Modal) => {
    this.stopListeningToLoginModal(loginModal);
    if (this.state === 'pending') {
      this.debug('dialog closed while pending causing logout');
      this.logout({ triggered: false });
    }
    this.debug('login closed');
  };

  onLoginMessage = (loginModal: Modal, signup: boolean, message: Message) => {
    this.debug('onLoginMessage', { loginModal, signup, message });
    const loginType = signup ? 'signup' : 'login';
    if (message.type === 'loggedin') {
      // we are done with the login modal at this point, make sure we don't
      // also listen for the closed event...
      this.stopListeningToLoginModal(loginModal);

      // Windows desktop does not close the window when running
      // windows.close() from login-success.ts, so we'll close it
      // here
      loginModal.close();

      // now check if the params are what we need...
      this.validateLogin(loginType, message);
    }
  };

  onLogoutMessage = (logoutModal: Modal, message: Message) => {
    this.debug('onLogoutMessage', { logoutModal, message });
    if (message.type === 'loggedout') {
      // we are done with the logout modal at this point, make sure we don't
      // also listen for the closed event...
      this.stopListeningToLoginModal(logoutModal);

      // Windows desktop does not close the window when running
      // windows.close() from logout-success.ts, so we'll close it
      // here
      logoutModal.close();
    }
  };

  onLogoutClosed = (logoutModal: Modal) => {
    this.stopListeningToLoginModal(logoutModal);
    this.debug('logout closed');
  };

  /**
   * User wants to log out!
   */
  onLogoutClick = async () => {
    this.settings?.clear();
    MetricService.increment('click.task_pane.logout.link');
    this.logout();
  };

  /**
   * User wants to sign up!
   */
  onSignupClick = async () => {
    MetricService.increment('click.task_pane.sign_up.button');
    this.resetStateParam();
    const params = new URLSearchParams({
      state: `${this.stateParam}`,
    });
    this.setState('pending');
    const loginModal = await modalManager.open(
      'login',
      `https://${window.location.host}/signup?${params.toString()}`,
      this.modalOptions,
    );
    this.listenToLoginModal(loginModal, true);
  };

  /**
   * Change the state param to a new value.
   *
   * State values expire after 10 minutes.
   */
  resetStateParam() {
    if (this.stateParamTimeout) {
      clearTimeout(this.stateParamTimeout);
    }

    this.stateParam = Math.floor(Math.random() * Math.floor(256874697));
    this.stateParamTimeout = setTimeout(() => {
      this.debug('state param timed out');
      this.stateParam = null;
      this.settings?.set('loginState', 0, true);
      this.stateParamTimeout = null;
    }, 60 * 1000 * 10);
    return this.stateParam;
  }

  setTrial(trial: Trial | null) {
    this.debug('setTrial()', trial);
    this.trialDetails = trial;
    this.settings?.set('trial', this.trialDetails);
    this.emit('trialUpdated', trial);
  }

  /**
   * Set our state flag to a new state
   */
  setState(state: LoginState) {
    this.debug(`setState(): current=${this.state}, new=${state}`);
    const previous = this.state;
    this.state = state;
    this.emit('state', state, previous);
  }

  setUser(user: User | null) {
    this.debug('setUser()', user);
    this.userDetails = user;
    if (this.userDetails) {
      this.settings?.set('user', this.userDetails);
    } else {
      this.logout({ triggered: false });
    }
    this.emit('userUpdated', user);
  }

  /**
   * Stop listening to the UserService specific listeners
   */
  stopListeningToLoginModal(loginModal: Modal) {
    Object.keys(this.loginListeners).forEach((eventName) => {
      const listener = this.loginListeners[eventName];
      loginModal.removeListener(eventName, listener);
    });
    this.loginListeners = {};
  }

  async validateLogin(loginType: 'signup' | 'login', message: Message) {
    this.debug('validateLogin: Validating login from message', { loginType, message });
    // now check if the params are what we need...
    if (this.validateParams(message)) {
      this.debug('validateLogin: valid login');
      return await this.loginFromMessage(message);
    } else {
      this.debug(`validateLogin: parameters could not be validated. invalid ${loginType}`);
      this.emit('error', `There was an error during ${loginType}, please try again.`);
      return Promise.resolve();
    }
  }

  /**
   * Validate params we got back from the login service.
   */
  validateParams(message: Message) {
    if (!message.params) {
      return false;
    }

    if (!this.stateParam) {
      this.debug('no state param is set. param may have timed out', this);
      return false;
    }

    if (!message.params.access_token || !message.params.state) {
      this.debug('missing access_token or state', message);
      return false;
    }

    if (message.params.state !== `${this.stateParam}`) {
      this.debug('param state does not match internal state param', message.params.state, this.stateParam);
      return false;
    }

    return true;
  }

  waitForInitComplete(): Promise<void> {
    this.initialize();

    if (this.initComplete) {
      this.debug('waitForInitComplete: init already complete');
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.debug('waitForInitComplete: waiting for init complete');
      this.on('init-complete', () => {
        this.debug('waitForInitComplete: init completed');
        resolve();
      });
    });
  }
}

export const userService = new UserService();
